/**
 * Browser entry point.
 *
 * Skia must finish loading CanvasKit before modules importing Skia are
 * evaluated. The native entry point remains in index.js.
 */

import {AppRegistry} from 'react-native';
import {LoadSkiaWeb} from '@shopify/react-native-skia/lib/module/web';
import canvasKitWasmUrl from 'canvaskit-wasm/bin/full/canvaskit.wasm';
import {
  isTelegramLaunch,
  migrateLegacyTelegramServiceWorker,
} from './src/core/infrastructure/browser/telegram-service-worker-migration';
import {primeBrowserAuthenticatedUser} from './src/core/infrastructure/authentication/browser-authentication-state';
import {
  createWebReleaseUpdateController,
  extractWebEntryAsset,
} from './src/core/infrastructure/browser/web-release-update-controller';
import {AsyncStorageKeyValueStorage} from './src/core/infrastructure/storage/async-storage-key-value-storage';
import {
  LANGUAGE_STORAGE_KEY,
  normalizeSupportedLanguage,
  persistServerLanguagePreference,
  resolveRuntimeLanguage,
  resolveSupportedLanguage,
} from './src/i18n/language-preference';
import {interfaceResources} from './src/i18n/resources';
import {applicationStartup} from './src/shared/application-startup';
import {createBrowserBootstrap} from './src/core/infrastructure/browser/browser-bootstrap';

const APP_NAME = 'NeiroDivinations';
const root = document.getElementById('root');
const telegram = window.Telegram?.WebApp;
const telegramLaunch = isTelegramLaunch({
  initData: telegram?.initData,
});
let telegramReleaseUpdateInstalled = false;
let startupLanguage = resolveSupportedLanguage([
  telegram?.initDataUnsafe?.user?.language_code,
  ...(navigator.languages || [navigator.language]),
]);
const bootstrapText = key => interfaceResources[startupLanguage].translation.bootstrap[key];
const bootstrap = createBrowserBootstrap({
  document,
  root,
  startup: applicationStartup,
  loadingText: () => bootstrapText('loadingOracle'),
  failureText: () => bootstrapText('startupError'),
  retryText: () => bootstrapText('retry'),
  retry: () => window.location.reload(),
});
applicationStartup.start();

window.__NEIRO_RUNTIME__ = telegramLaunch ? 'telegram' : 'default';

class TelegramAuthenticationError extends Error {}

const persistAuthenticatedLocale = async locale => {
  const language = normalizeSupportedLanguage(locale);
  if (!language) return;
  startupLanguage = language;

  window.__NEIRO_BOOTSTRAP_LOCALE__ = language;
  try {
    await persistServerLanguagePreference(
      AsyncStorageKeyValueStorage,
      language,
    );
  } catch {
    // The server preference still remains authoritative and will be restored
    // on the next authenticated bootstrap if browser storage is unavailable.
  }
};

const syncAuthenticatedPreferences = async (user, fallbackLocale) => {
  const timezone =
    Intl.DateTimeFormat().resolvedOptions().timeZone || user?.timezone || 'UTC';
  const locale = user?.locale || fallbackLocale || 'ru';
  await persistAuthenticatedLocale(locale);
  try {
    const response = await fetch('/api/v1/me/preferences', {
      method: 'PATCH',
      credentials: 'same-origin',
      headers: {'Content-Type': 'application/json', Accept: 'application/json'},
      body: JSON.stringify({locale, timezone}),
    });
    if (response.ok) {
      const synchronizedUser = await response
        .json()
        .catch(() => ({...user, locale, timezone}));
      await persistAuthenticatedLocale(synchronizedUser?.locale);
      return synchronizedUser;
    }
  } catch {
    // Existing authenticated sessions remain usable while preference sync retries
    // on the next bootstrap.
  }
  return user;
};

const renderBootstrap = (message, retry) => bootstrap.show(message, retry);

const registerServiceWorker = () => {
  if (
    process.env.NODE_ENV !== 'production' ||
    window.__NEIRO_RUNTIME__ === 'telegram' ||
    !('serviceWorker' in navigator)
  ) {
    return;
  }

  const register = () => {
    navigator.serviceWorker
      .register('/service-worker.js', {updateViaCache: 'none'})
      .catch(error => {
        console.warn('Service worker registration failed', error);
      });
  };
  if (document.readyState === 'complete') {
    register();
  } else {
    window.addEventListener('load', register, {once: true});
  }
};

const installTelegramReleaseUpdateCheck = () => {
  if (telegramReleaseUpdateInstalled) return;

  const currentEntryAsset = extractWebEntryAsset(
    document.documentElement.innerHTML,
  );
  if (!currentEntryAsset) return;

  telegramReleaseUpdateInstalled = true;
  const checkForReleaseUpdate = createWebReleaseUpdateController({
    currentEntryAsset,
    fetchCurrentHtml: async () => {
      const url = new URL('/index.html', window.location.href);
      url.searchParams.set('releaseCheck', Date.now().toString());
      const response = await fetch(url, {
        cache: 'no-store',
        credentials: 'same-origin',
        headers: {Accept: 'text/html'},
      });
      if (!response.ok) {
        throw new Error(`Release check failed with HTTP ${response.status}`);
      }
      return response.text();
    },
    reload: () => window.location.reload(),
  });
  const handleActivation = () => {
    checkForReleaseUpdate().catch(() => undefined);
  };
  const handleVisibilityChange = () => {
    if (document.visibilityState === 'visible') handleActivation();
  };

  telegram?.onEvent?.('activated', handleActivation);
  document.addEventListener('visibilitychange', handleVisibilityChange);
  handleActivation();
};

const authenticateTelegram = async () => {
  window.__NEIRO_RUNTIME__ = 'telegram';
  telegram.ready();
  telegram.expand();
  document.documentElement.style.colorScheme = telegram.colorScheme || 'dark';

  let authenticatedUser;
  try {
    const sessionResponse = await fetch('/api/v1/me', {
      credentials: 'same-origin',
      headers: {Accept: 'application/json'},
    });
    if (sessionResponse.ok) {
      authenticatedUser = await sessionResponse.json();
    }
  } catch {
    // A valid initData payload can still establish the session below.
  }

  if (!authenticatedUser) {
    const authResponse = await fetch('/api/v1/auth/telegram', {
      method: 'POST',
      credentials: 'same-origin',
      headers: {'Content-Type': 'application/json', Accept: 'application/json'},
      body: JSON.stringify({init_data: telegram.initData}),
    });
    if (!authResponse.ok) {
      if (authResponse.status === 401) {
        throw new TelegramAuthenticationError(
          bootstrapText('staleTelegram'),
        );
      }
      throw new Error(bootstrapText('authTelegramError'));
    }
    const auth = await authResponse.json();
    authenticatedUser = auth.user;
  }

  const languageCode = telegram.initDataUnsafe?.user?.language_code;
  await syncAuthenticatedPreferences(
    authenticatedUser,
    normalizeSupportedLanguage(languageCode) ?? 'en',
  );
};

const resolveRuntime = async () => {
  if (telegramLaunch) {
    const migration = await migrateLegacyTelegramServiceWorker();
    if (migration.reloadScheduled) return false;
    if (migration.blockedByLegacyController) {
      throw new Error(
        bootstrapText('updateTelegramError'),
      );
    }
    if (!telegram?.initData) {
      throw new TelegramAuthenticationError(
        bootstrapText('missingTelegramData'),
      );
    }
    await authenticateTelegram();
    installTelegramReleaseUpdateCheck();
    return true;
  }
  try {
    const response = await fetch('/api/v1/me', {
      credentials: 'same-origin',
      headers: {Accept: 'application/json'},
    });
    if (response.ok) {
      const user = await response.json();
      const synchronizedUser = await syncAuthenticatedPreferences(
        user,
        user.locale,
      );
      primeBrowserAuthenticatedUser(synchronizedUser);
      window.__NEIRO_RUNTIME__ = 'web-authenticated';
    } else if (response.status === 401) {
      primeBrowserAuthenticatedUser(null);
    }
  } catch {
    // The local PWA remains available when the API is offline.
  }
  return true;
};

const mountApplication = async () => {
  const savedLanguage = await AsyncStorageKeyValueStorage.getString(LANGUAGE_STORAGE_KEY).catch(() => null);
  startupLanguage = resolveRuntimeLanguage(window.__NEIRO_BOOTSTRAP_LOCALE__, savedLanguage, startupLanguage);
  renderBootstrap(bootstrapText('loadingSpace'));
  const shouldMount = await resolveRuntime();
  if (!shouldMount) return;
  renderBootstrap(bootstrapText('loadingOracle'));
  await LoadSkiaWeb({locateFile: () => canvasKitWasmUrl});
  const App = (await import('./App')).default;

  AppRegistry.registerComponent(APP_NAME, () => App);
  AppRegistry.runApplication(APP_NAME, {rootTag: root});
};

const start = () => {
  mountApplication().catch(error => {
    const message = error instanceof Error ? error.message : bootstrapText('startupError');
    const retry = error instanceof TelegramAuthenticationError ? undefined : start;
    renderBootstrap(message, retry);
  });
};

registerServiceWorker();
start();
